export const AUTH_ORIGIN_ROUTE_STORAGE_KEY = 'auth_origin_route';

export const AUTH_PAGE_PATHS = ['/login', '/cadastro'] as const;

type LocationLike = {
	pathname: string;
	search?: string;
};

const getWindowOrigin = () => {
	if (typeof window === 'undefined') return 'http://localhost';

	return window.location.origin;
};

export const getRoutePathWithSearch = ({ pathname, search = '' }: LocationLike) =>
	`${pathname}${search}`;

export const buildAuthRedirectPath = (authPath: string, redirectTo: string) =>
	`${authPath}?redirect=${encodeURIComponent(redirectTo || '/')}`;

export const buildLoginRedirectPath = (redirectTo: string) =>
	buildAuthRedirectPath('/login', redirectTo);

export const buildSignupRedirectPath = (redirectTo: string) =>
	buildAuthRedirectPath('/cadastro', redirectTo);

export const isAuthPagePathname = (pathname?: string | null) =>
	Boolean(pathname && AUTH_PAGE_PATHS.includes(pathname as any));

export const getPathnameFromRoute = (route?: string | null) => {
	if (!route) return '';

	try {
		return new URL(route, getWindowOrigin()).pathname;
	} catch {
		return '';
	}
};

export const getRelativePathFromUrl = (url?: string | null) => {
	if (!url) return undefined;

	try {
		const parsed = new URL(url, getWindowOrigin());

		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch {
		return undefined;
	}
};

export const getCleanRouteWithoutGoogleOAuthParams = (
	route?: string | null
) => {
	try {
		const routeUrl = new URL(route || '/', getWindowOrigin());

		routeUrl.searchParams.delete('code');
		routeUrl.searchParams.delete('state');

		return `${routeUrl.pathname}${routeUrl.search}${routeUrl.hash}`;
	} catch {
		return '/';
	}
};

export const getRedirectParamFromRoute = (route?: string | null) => {
	if (!route) return null;

	try {
		const routeUrl = new URL(route, getWindowOrigin());

		return routeUrl.searchParams.get('redirect');
	} catch {
		return null;
	}
};

export const getStoredAuthOriginRoute = () => {
	if (typeof window === 'undefined') return null;

	try {
		return window.localStorage.getItem(AUTH_ORIGIN_ROUTE_STORAGE_KEY);
	} catch {
		return null;
	}
};

export const setStoredAuthOriginRoute = (route: string) => {
	if (typeof window === 'undefined') return;

	try {
		window.localStorage.setItem(AUTH_ORIGIN_ROUTE_STORAGE_KEY, route);
	} catch {
		// ignore storage errors
	}
};

export const clearStoredAuthOriginRoute = () => {
	if (typeof window === 'undefined') return;

	try {
		window.localStorage.removeItem(AUTH_ORIGIN_ROUTE_STORAGE_KEY);
	} catch {
		// ignore storage errors
	}
};

export const resolveAuthPageReturnRoute = ({
	currentRoute,
	responseRedirectPath,
	savedOriginRoute,
}: {
	currentRoute: string;
	responseRedirectPath?: string | null;
	savedOriginRoute?: string | null;
}) => {
	const savedOriginPathname = getPathnameFromRoute(savedOriginRoute);
	const responseRedirectPathname = getPathnameFromRoute(responseRedirectPath);
	const currentPathname = getPathnameFromRoute(currentRoute);

	if (isAuthPagePathname(savedOriginPathname)) {
		return getCleanRouteWithoutGoogleOAuthParams(savedOriginRoute);
	}

	if (isAuthPagePathname(responseRedirectPathname)) {
		return getCleanRouteWithoutGoogleOAuthParams(responseRedirectPath);
	}

	if (isAuthPagePathname(currentPathname)) {
		return getCleanRouteWithoutGoogleOAuthParams(currentRoute);
	}

	return null;
};

export const resolvePostAuthRedirectPath = ({
	fallback = '/',
	responseRedirectPath,
	savedOriginRoute,
}: {
	fallback?: string;
	responseRedirectPath?: string | null;
	savedOriginRoute?: string | null;
}) => {
	const redirectParam = getRedirectParamFromRoute(savedOriginRoute);

	return redirectParam || responseRedirectPath || fallback;
};
